import { defineStore } from 'pinia';
import { shouldThrottle } from '@/util/rateLimit.js';
import apiClient from '@/util/apiClient';
import posthog from '@/composables/usePostHog.js';

export const useGroupMembershipStore = defineStore('groupMembershipStore', {
	state: () => ({
		groupMembership: [],
		usage: []
	}),
	actions: {
		/**
		 * Add a user to a group
		 * ROUTE: REFEREE ADMIN
		 * @param payload
		 * @returns {Promise<void>}
		 */
		async addUserToGroup(payload) {
			if (shouldThrottle('addUserToGroup', 100)) {
				posthog.capture('throttled_action', { action: 'addUserToGroup' });
				return;
			}
			await apiClient.post('/memberships/add', payload);
		},
		async inviteUserToGroup(groupNumber, payload) {
			if (shouldThrottle('inviteUserToGroup', 100)) {
				posthog.capture('throttled_action', { action: 'inviteUserToGroup' });
				return;
			}
			const response = await apiClient.post(`/invitations/group/${groupNumber}/invite-user`, payload);
			return response.data;
		},
		/**
		 * updates the users roles within a group
		 * ROUTE: REFEREE ADMIN
		 * @param payload
		 * @returns {Promise<any>}
		 */
		async updateRoles(payload) {
			if (shouldThrottle('updateRoles', 100)) {
				posthog.capture('throttled_action', { action: 'updateRoles' });
				return;
			}
			const response = await apiClient.patch('/memberships/update', payload);
			return response.data;
		},
		/**
		 * allows a user to join a group.
		 * if the user is an official, with the 'referee_pathway' flag from the US Soccer Learning Center
		 * then add the user as an official
		 * ROUTE: AUTHENTICATED
		 * @param groupNumber
		 * @param official if the user is a part of the US Soccer referee_pathway
		 * @returns {Promise<void>}
		 */
		async joinGroupAsUser(groupNumber, official) {
			if (shouldThrottle('addUserToGroup', 100)) {
				posthog.capture('throttled_action', { action: 'joinGroupAsUser' });
				return;
			}
			if (!official) await apiClient.post(`/memberships/join/${groupNumber}?official=true`);
			else await apiClient.post(`/memberships/join/${groupNumber}?official=true`);
		},
		/**
		 * allows a user to leave a group
		 * ROUTE: AUTHENTICATED
		 * @param groupNumber
		 * @returns {Promise<void>}
		 */
		async leaveGroupAsUser(groupNumber) {
			if (shouldThrottle('leaveGroupAsUser', 100)) {
				posthog.capture('throttled_action', { action: 'leaveGroupAsUser' });
				return;
			}
			await apiClient.post(`/memberships/leave/${groupNumber}`);
		},
		/**
		 * strips a user of their roles and removes them from the group
		 * ROUTE: REFEREE ADMIN
		 * @param payload
		 * @returns {Promise<void>}
		 */
		async removeUserFromGroup(payload) {
			if (shouldThrottle('removeUserFromGroup', 100)) {
				posthog.capture('throttled_action', { action: 'removeUserFromGroup' });
				return;
			}
			await apiClient.post('/memberships/remove', payload);
		},
		/**
		 * Gets the usage of users for the group and returns them as a number.
		 * ROUTE: REFEREE ADMIN
		 * @param groupNumber
		 * @returns {Promise<void>} current num of users in role and max number allowed based on the groups plan
		 */
		async groupUsage(groupNumber) {
			if (shouldThrottle('groupUsage', 100)) {
				posthog.capture('throttled_action', { groupNumber });
				return;
			}
			const response = await apiClient.get(`/memberships/${groupNumber}/usage`);
			this.usage = response.data;
		},
		/**
		 * Recalculates the total usage for the group and returns the new value
		 * ROUTE: REFEREE ADMIN
		 * @param groupNumber
		 * @returns {Promise<any>} current num of users in role and max number allowed based on the groups plan
		 */
		async recalculateCounters(groupNumber) {
			if (shouldThrottle('recalculateCounters', 500)) {
				posthog.capture('throttled_action', { action: 'recalculateCounters' });
				return;
			}
			const response = await apiClient.post(`/memberships/${groupNumber}/recalculate`);
			return response.data;
		},
		async getInvitationsForGroup(groupNumber, options = {}) {
			if (shouldThrottle('groupUsage', 100)) {
				posthog.capture('throttled_action', { groupNumber });
				return;
			}
			const response = await apiClient.get(`/invitations/group/${groupNumber}`, {
				params: {
					page: options.page ?? 0,
					size: options.size ?? 1000,
					search: options.search?.trim() || undefined,
					status: options.status || 'all'
				}
			});

			return response.data;
		}
	}
});
